import { logger } from '../services/logger';
import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, MessageCircle, ChevronDown, ChevronUp, User, Calendar } from 'lucide-react';
import { supabase } from '../lib/db';
import { useAuth } from '../lib/auth';
import { MarketStatus } from '../types';

// Define a more specific type for market_options
interface MarketOption {
  id?: string;
  name?: string;
  option_name?: string;
  status?: string;
  trading_end_time?: string;
}

interface Market {
  id: string;
  title: string;
  type?: 'binary' | 'multiple';
  category: string;
  description: string;
  outcomes?: { name: string }[];
  market_options?: MarketOption[]; // Use the specific type instead of any
  rules: string;
  resolution_date?: string;
  created_by: string;
  status: MarketStatus;
  created_at: string;
  cover_image?: string;
  image_url?: string;
  imageUrl?: string;
  engine_type?: string | null;
  lmsr_b?: number | null;
  creator?: {
    email: string;
    raw_user_meta_data: {
      first_name?: string;
      last_name?: string;
    };
  };
  invites?: {
    email: string;
  }[];
}

// Add a constant for final states
const FINAL_STATES: MarketStatus[] = ['ended', 'expired', 'cancelled', 'resolved'];

type LiquidityTierChoice = 'standard' | 'enhanced' | 'premium';

/** Must match `approve_pending_lmsr_market` in 20260430120000_lmsr_liquidity_tiers_b_500_1500_5000.sql */
const LMSR_TIER_B: Record<LiquidityTierChoice, number> = {
  standard: 500,
  enhanced: 1500,
  premium: 5000,
};

function isLmsrPendingAwaitingLiquidity(market: Market): boolean {
  if (market.status !== 'pending_approval') return false;
  if ((market.engine_type ?? '').toLowerCase() !== 'lmsr') return false;
  const b = market.lmsr_b;
  return b == null || Number(b) <= 0;
}

export function AdminMarketRequests() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<MarketStatus>('pending_approval');
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedMarket, setExpandedMarket] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [liquidityModalMarket, setLiquidityModalMarket] = useState<Market | null>(null);
  const [approveTier, setApproveTier] = useState<LiquidityTierChoice>('standard');

  useEffect(() => {
    if (!user) return;
    logger.info(`useEffect triggered. Active tab: ${activeTab}`);

    const fetchMarkets = async () => {
      logger.info(`fetchMarkets started for tab: ${activeTab}`);
      try {
        setLoading(true);
        const { data, error: marketError } = await supabase
          .from('markets')
          .select('*')
          .eq('status', activeTab)
          .order('created_at', { ascending: false });

        if (marketError) throw marketError;
        
        // Just use the market data directly without trying to join
        const marketsData = data || [];
        
        // Add empty invites array to each market to prevent undefined errors
        const marketsWithDefaults = marketsData.map(market => ({
          ...market,
          invites: [],
          outcomes: market.market_options || market.outcomes || []
        }));
        
        setMarkets(marketsWithDefaults);
        setError(null);
        logger.info(`fetchMarkets finished for tab: ${activeTab}. Found ${marketsWithDefaults.length} markets.`);
      } catch (err) {
        logger.error('Error fetching markets:', err);
        setError('Failed to load markets');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkets();
  }, [user, activeTab]);

  const handleAccept = async (marketId: string) => {
    setError(null);
    const market = markets.find(m => m.id === marketId);
    if (!market) {
      setError('Market not found');
      return;
    }
    if (FINAL_STATES.includes(market.status)) {
      setError(`Cannot accept market in ${market.status} state`);
      return;
    }

    if (isLmsrPendingAwaitingLiquidity(market)) {
      setApproveTier('standard');
      setLiquidityModalMarket(market);
      return;
    }

    setActionLoading(true);
    logger.info(`Attempting to accept market: ${marketId}`);
    try {
      const { data, error } = await supabase
        .from('markets')
        .update({
          status: 'active',
        })
        .eq('id', marketId)
        .select();

      logger.error('Supabase accept response:', { data, error });

      if (error) throw error;

      if (!data || data.length === 0) {
        logger.warn('Market accept update did not return data. RLS issue?');
        setError('Failed to accept market. Update might be blocked.');
        return;
      }

      logger.info('Market accepted successfully, refreshing list...');
      setActiveTab('active');
      setExpandedMarket(null);
    } catch (err) {
      logger.error('Error accepting market:', err);
      setError('Failed to accept market');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmLiquidityApprove = async () => {
    if (!liquidityModalMarket) return;
    setActionLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.rpc('approve_pending_lmsr_market', {
        p_market_id: liquidityModalMarket.id,
        p_liquidity_tier: approveTier,
      });

      if (error) throw error;

      const payload = data as { success?: boolean; error?: string } | null;
      if (!payload || payload.success !== true) {
        setError(payload?.error || 'Approval failed');
        return;
      }

      setLiquidityModalMarket(null);
      setExpandedMarket(null);
      setActiveTab('active');
    } catch (err) {
      logger.error('Error approving LMSR market with liquidity:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve market');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async (marketId: string) => {
    setActionLoading(true);
    logger.info(`Attempting to decline market: ${marketId}`);
    try {
      // Find the current market
      const market = markets.find(m => m.id === marketId);
      if (!market) {
        setError('Market not found');
        return;
      }

      // Check if market is in a final state
      if (FINAL_STATES.includes(market.status)) {
        setError(`Cannot reject market in ${market.status} state`);
        return;
      }

      const { data, error } = await supabase
        .from('markets')
        .update({ 
          status: 'rejected'
        })
        .eq('id', marketId)
        .select();

      logger.error('Supabase decline response:', { data, error });

      if (error) throw error;

      if (!data || data.length === 0) {
        logger.warn('Market decline update did not return data. RLS issue?');
        setError('Failed to decline market. Update might be blocked.');
        return;
      }

      logger.info('Market declined successfully, refreshing list...');
      setActiveTab('rejected');
      setExpandedMarket(null);
    } catch (err) {
      logger.error('Error declining market:', err);
      setError('Failed to decline market');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <h1 className="text-3xl font-bold mb-8">Market Creation Requests</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex gap-4 border-b">
          <button
            onClick={() => setActiveTab('pending_approval')}
            className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'pending_approval'
                ? 'text-wayja-teal border-wayja-teal'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <Clock className="w-4 h-4" />
            Pending
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'active'
                ? 'text-wayja-teal border-wayja-teal'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            Active
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'rejected'
                ? 'text-wayja-teal border-wayja-teal'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            <XCircle className="w-4 h-4" />
            Rejected
          </button>
        </div>
      </div>

      {/* Markets List */}
      <div className="space-y-4">
        {markets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
              {activeTab === 'pending_approval' && <Clock className="w-full h-full" />}
              {activeTab === 'active' && <CheckCircle className="w-full h-full" />}
              {activeTab === 'rejected' && <XCircle className="w-full h-full" />}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab === 'pending_approval' ? 'pending' : activeTab} markets
            </h3>
            <p className="text-gray-500">
              {activeTab === 'pending_approval'
                ? 'There are no pending market creation requests'
                : activeTab === 'active'
                ? 'No markets have been approved yet'
                : 'No markets have been rejected yet'}
            </p>
          </div>
        ) : (
          markets.map((market) => (
            <div key={market.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Market Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedMarket(
                  expandedMarket === market.id ? null : market.id
                )}
              >
                <div className="flex justify-between items-start">
                  <div className="flex gap-4">
                    <img
                      src={market.image_url || market.cover_image || undefined}
                      alt={market.title}
                      className="w-16 h-16 object-cover rounded-lg bg-gray-200"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null; // Prevent infinite error loops
                        // Just hide the broken image
                        target.style.display = 'none';
                        // Replace with a div showing the first letter of the title
                        const parent = target.parentElement;
                        if (parent) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'w-16 h-16 bg-wayja-teal flex items-center justify-center rounded-lg';
                          placeholder.innerHTML = `<span class="text-white text-xl font-bold">${market.title?.[0] || '?'}</span>`;
                          parent.insertBefore(placeholder, target);
                        }
                      }}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {market.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {market.created_by || "Unknown user"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(market.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-wayja-teal/10 text-wayja-teal text-sm font-medium rounded-full">
                      {market.category}
                    </span>
                    {expandedMarket === market.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedMarket === market.id && (
                <div className="border-t p-4">
                  {/* Market Details */}
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                      <p className="text-gray-600">{market.description || 'No description available'}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Rules</h4>
                      <p className="text-gray-600">{market.rules || 'No rules specified'}</p>
                    </div>
                  </div>

                  {/* Outcomes */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">Outcomes</h4>
                    <div className="space-y-2">
                      {(() => {
                        // Handle different outcome formats
                        if (market.outcomes && Array.isArray(market.outcomes)) {
                          return market.outcomes.map((outcome, index) => (
                            <div key={index} className="p-2 bg-gray-50 rounded-md">
                              {typeof outcome === 'object' && outcome.name ? outcome.name : outcome.toString()}
                            </div>
                          ));
                        } else if (market.market_options && Array.isArray(market.market_options)) {
                          return market.market_options.map((option, index) => (
                            <div key={index} className="p-2 bg-gray-50 rounded-md">
                              {option.name || option.option_name || `Option ${index + 1}`}
                            </div>
                          ));
                        } else {
                          return (
                            <div className="p-2 bg-gray-50 rounded-md text-gray-500">
                              No outcomes available
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>

                  {activeTab === 'pending_approval' ? (
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => handleDecline(market.id)}
                        disabled={actionLoading || FINAL_STATES.includes(market.status)}
                        className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleAccept(market.id)}
                        disabled={actionLoading || FINAL_STATES.includes(market.status)}
                        className="flex items-center gap-2 px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 p-4 bg-gray-50 rounded-md">
                      <div className="flex items-start gap-3">
                        <MessageCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500 mb-2">
                            {activeTab === 'active' ? 'Approved' : 'Rejected'}
                            
                          </p>
                          
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {liquidityModalMarket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="liquidity-modal-title"
          >
            <h3 id="liquidity-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
              Set liquidity depth
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose the tier for this share-priced market. Wayja&apos;s platform wallet funds the LMSR collateral
              (b × ln(number of outcomes)); the market will become active.
            </p>
            <p className="text-sm font-medium text-gray-800 mb-3">{liquidityModalMarket.title}</p>
            <div className="grid grid-cols-1 gap-2 mb-6">
              {(['standard', 'enhanced', 'premium'] as const).map((tier) => (
                <label
                  key={tier}
                  className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                    approveTier === tier
                      ? 'border-wayja-teal ring-1 ring-wayja-teal bg-wayja-teal/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="approveTier"
                    checked={approveTier === tier}
                    onChange={() => setApproveTier(tier)}
                    className="text-wayja-teal focus:ring-wayja-teal"
                  />
                  <div>
                    <span className="font-medium text-gray-900 capitalize">{tier}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      b = {LMSR_TIER_B[tier].toLocaleString()}
                    </span>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (!actionLoading) setLiquidityModalMarket(null);
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmLiquidityApprove()}
                disabled={actionLoading}
                className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
              >
                {actionLoading ? 'Working…' : 'Approve & activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
